import type { DocumentData } from 'firebase/firestore';
import { IProject, IPin } from '../../types';
import { IMember } from '../../types/organisation';
import { hasRole } from '../../utilities/roleUtils';
import { toDate } from '../../utilities/timestampUtils';

/**
 * Pure helpers for project data shaping, extracted from ProjectManager so the
 * normalisation and access rules can be unit tested without Firestore. All
 * functions are side-effect free and never mutate their inputs.
 */

/**
 * Maps a Firestore document (id + data) to an IProject. The document data is
 * spread after the id, so a `project_id` field stored on the document wins
 * over the document id, matching the original inline behaviour.
 */
export const projectFromDoc = (id: string, data: DocumentData): IProject =>
  ({ project_id: id, ...data } as IProject);

/**
 * Whether a pin should be treated as a QR-enabled pin: the project must have
 * QR enabled and the pin must be of type `qrCode`.
 */
export const isQRCodePinEnabled = (
  project: IProject | null,
  pin: { pinType?: string | null }
): boolean => {
  if (project && project.isQREnabled && pin.pinType === 'qrCode') {
    return true;
  }
  return false;
};

/** Pins belonging to the given project. */
export const filterPinsByProject = (pins: IPin[], project_id: string): IPin[] =>
  pins.filter(pin => pin.project === project_id);

/** New audio size total, treating a missing current size as zero. */
export const calculateUpdatedAudioSize = (
  currentAudioSize: number | undefined,
  additionalSize: number
): number => (currentAudioSize || 0) + additionalSize;

/** Subject tags for a project, defaulting to an empty array. */
export const getSubjectTags = (projectData: IProject): string[] =>
  projectData.tags?.subjectTag || [];

/**
 * Adds a tag to the subject tags, de-duplicating existing entries while
 * preserving insertion order.
 */
export const addSubjectTag = (
  existingTags: string[] | undefined,
  tagName: string
): string[] => {
  const subjectTags = new Set(existingTags || []);
  subjectTags.add(tagName);
  return Array.from(subjectTags);
};

/**
 * Removes a tag from the subject tags. Returns the updated (de-duplicated)
 * array, or null when the tag was not present and no write is needed.
 */
export const removeSubjectTag = (
  existingTags: string[] | undefined,
  tagName: string
): string[] | null => {
  const subjectTags = new Set(existingTags || []);
  if (!subjectTags.has(tagName)) {
    return null;
  }
  subjectTags.delete(tagName);
  return Array.from(subjectTags);
};

/**
 * Document payload for a newly created project: audio size starts at zero and
 * `isResponseEnabled` defaults to false when falsy.
 */
export const buildCreateProjectPayload = (projectData: IProject) => ({
  ...projectData,
  audioSize: 0,
  isResponseEnabled: projectData.isResponseEnabled || false,
});

/** Maps settings fields to dotted `settings.<key>` Firestore update paths. */
export const buildSettingsFieldUpdates = (
  settingsFields: Record<string, unknown>
): Record<string, unknown> => {
  const updateData: Record<string, unknown> = {};
  Object.entries(settingsFields).forEach(([key, value]) => {
    updateData[`settings.${key}`] = value;
  });
  return updateData;
};

/** Stored project config, or the default config when falsy. */
export const resolveProjectConfig = (config: unknown) =>
  config || { locationInfo: true, authorInfo: true, recordingInfo: true, uploadFields: true, password: '' };

/** Public invite link for an invite document id. */
export const buildInviteLink = (inviteId: string): string =>
  `https://admin.theoverhear.app/invite/${inviteId}`;

/**
 * Resolves an invite document to its project access details, or null when the
 * invite has no resolvable expiration date or has expired.
 */
export const resolveValidInvite = (
  inviteData: DocumentData,
  now: Date
): { project_id: string; accessLevel: string } | null => {
  const expirationDate = toDate(inviteData.expirationDate);
  if (expirationDate && now < expirationDate) {
    return { project_id: inviteData.project_id, accessLevel: inviteData.accessLevel };
  }
  return null;
};

/**
 * Appends a pin key to a project's pins. Returns the updated array, or null
 * when the pin is already present and no write is needed.
 */
export const addPinKey = (
  existingPins: string[] | undefined,
  pinKey: string
): string[] | null => {
  const pins = existingPins || [];
  if (pins.includes(pinKey)) {
    return null;
  }
  return [...pins, pinKey];
};

/** User keys of members holding the moderator or organisationAdmin role. */
export const getModeratorAndAdminUserKeys = (members: IMember[]): string[] =>
  members
    .filter((member: IMember) =>
      hasRole(member.roles, 'moderator') || hasRole(member.roles, 'organisationAdmin')
    )
    .map((member: IMember) => member.userKey);

/** Group ids added and removed between the old and new group lists. */
export const diffGroupIds = (
  oldGroupIds: string[],
  newGroupIds: string[]
): { addedGroupIds: string[]; removedGroupIds: string[] } => ({
  addedGroupIds: newGroupIds.filter(id => !oldGroupIds.includes(id)),
  removedGroupIds: oldGroupIds.filter(id => !newGroupIds.includes(id)),
});

/** Whether the user belongs to any group that grants access to the project. */
export const hasAccessViaGroups = (
  userGroups: string[],
  projectGroups: string[]
): boolean => userGroups.some(g => projectGroups.includes(g));

/** Maps a ProjectAccess document (id + data) to an access record. */
export const toProjectAccessRecord = (id: string, data: DocumentData) => ({
  id,
  userId: data.userId,
  project_id: data.project_id,
  accessLevel: data.accessLevel,
  createdAt: data.createdAt,
});

/**
 * User details for a project access record. Pass null user data when the user
 * document does not exist; the name falls back through the profile name, the
 * legacy top-level name, then 'Unknown User'.
 */
export const buildAccessUserDetails = (
  userId: string,
  userData: DocumentData | null
): { userName: string; userKey: string; email: string } => {
  if (!userData) {
    return { userName: 'Unknown User', userKey: userId, email: '' };
  }
  return {
    userName: userData.userProfile?.userName || userData.userName || 'Unknown User',
    userKey: userId,
    email: userData.email || '',
  };
};
